import { useAdminAreaById } from "@/api/adminArea/userAdminAreaAPI";
import { AdministrativeArea } from "@/types/adminArea";
import { SpatialAggregationType } from "@/types/common";
import { EOutputAnalysisType } from "@/types/spatial";
import { GetAnalysisTemplateDetail } from "@/types/template";

import { useDeepCompareMemo } from "use-deep-compare";

import ConfigurationDataset from "./ConfigurationDataset";

const LABEL_DATASET_TYPE: Record<SpatialAggregationType, string> = {
  poi: "Point of Interest",
  polygon: "Polygon",
  adm_area: "Administrative Area",
  grid: "Grid",
  site_profiling: "Site Profiling",
  H3: "H3",
  geohash: "Geohash",
  line: "Line"
};

interface TemplateOverviewModalConfigurationProps {
  readonly templateDetail: GetAnalysisTemplateDetail;
}

export default function TemplateOverviewModalConfiguration({
  templateDetail
}: TemplateOverviewModalConfigurationProps) {
  const isHasAdminArea =
    templateDetail.configuration.area_input?.adm_area_id !== undefined;

  const { data: adminArea } = useAdminAreaById(
    templateDetail.configuration.area_input?.adm_area_id ?? null,
    undefined,
    { enable: isHasAdminArea }
  );

  const datasets = useDeepCompareMemo(() => {
    const initialDatasets: Record<
      SpatialAggregationType,
      GetAnalysisTemplateDetail["configuration"]["datasets"]
    > = {
      poi: [],
      polygon: [],
      adm_area: [],
      grid: [],
      site_profiling: [],
      H3: [],
      geohash: [],
      line: []
    };

    // Our efficient, single-pass logic from before
    return templateDetail.configuration.datasets.reduce(
      (accumulator, dataset) => {
        const type = dataset.spatial_aggregation_type;
        if (type in accumulator) {
          accumulator[type].push(dataset);
        }
        return accumulator;
      },
      initialDatasets
    );
  }, [templateDetail.configuration.datasets]);

  const templateAttributes = useDeepCompareMemo(() => {
    return (
      templateDetail.configuration.datasets?.reduce(
        (acc, dataset) => {
          const totalWeight = dataset.attributes.reduce(
            (sum, attr) => sum + (attr.weight_score ?? 0),
            0
          );
          acc[dataset.name] = totalWeight;
          return acc;
        },
        {} as Record<string, number>
      ) ?? {}
    );
  }, [templateDetail.configuration.datasets]);

  const formatLocationNames = () => {
    const locations =
      templateDetail.configuration.area_input.catchment?.locations || [];
    return locations
      .map((location: { name: string }) => location.name)
      .reduce((acc: string, name: string, i: number, arr: string[]) => {
        if (i === 0) return name;
        if (i === arr.length - 1) return `${acc} and ${name}`;
        return `${acc}, ${name}`;
      }, "");
  };

  const formatRadiusCatchment = () => {
    const contours =
      templateDetail.configuration.area_input.catchment?.contours || [];
    if (contours.length === 0) return 0;

    // Convert meters to kilometers and round to nearest integer
    const kmContours = contours
      .map((meters) => Math.round(meters / 1000))
      .sort((a, b) => a - b);

    // Format using reduce like formatLocationNames
    return kmContours
      .map((km) => `${km} km`)
      .reduce((acc: string, value: string, i: number, arr: string[]) => {
        if (i === 0) return value;
        if (i === arr.length - 1) return `${acc} and ${value} radius`;
        return `${acc}, ${value}`;
      }, "");
  };

  const formatTimeCatchment = () => {
    const contours =
      templateDetail.configuration.area_input.catchment?.contours || [];
    if (contours.length === 0) return 0;

    // Convert seconds to minutes and round to nearest integer
    const minuteContours = contours
      .map((seconds) => Math.round(seconds / 60))
      .sort((a, b) => a - b);

    // Format using reduce like formatLocationNames
    return minuteContours
      .map((min) => `${min} min`)
      .reduce((acc: string, value: string, i: number, arr: string[]) => {
        if (i === 0) return value;
        if (i === arr.length - 1) return `${acc} and ${value}`;
        return `${acc}, ${value}`;
      }, "");
  };

  const getAdminAreaHierarchyLabel = () => {
    if (!adminArea?.data?.[0]) return "";

    const area = adminArea.data[0];
    const hierarchy = [area.name];

    if (area.parents && area.parents.length > 0) {
      const parentNames = area.parents
        .map((parent: AdministrativeArea) => parent.name)
        .reverse();
      hierarchy.unshift(...parentNames);
    }

    return hierarchy.join(" > ");
  };

  return (
    <div>
      <div className="font-semibold text-txtHeading mb-2">Configuration</div>
      <div className="space-y-4">
        <div>
          <p className="text-txtHeading text-sm font-semibold">Datasets</p>
          <div className="*:leading-5 *:text-dim-gray *:text-xs">
            <p>Curated Datasets Aligned with Target Audience:</p>

            {Object.entries(datasets).map(
              ([key, value]) =>
                value.length > 0 && (
                  <div key={key as SpatialAggregationType} className="mb-4">
                    <p>
                      <b>
                        {LABEL_DATASET_TYPE[key as SpatialAggregationType]}:
                      </b>
                    </p>
                    <ul className="list-disc list-outside pl-4">
                      {value.map((dataset) => (
                        <ConfigurationDataset
                          key={dataset.id}
                          dataset={dataset}
                        />
                      ))}
                    </ul>
                  </div>
                )
            )}
          </div>
        </div>

        <div>
          <p className="text-txtHeading text-sm font-semibold">Input Area</p>
          <div className="*:leading-5 *:text-dim-gray *:text-xs">
            <p>
              Define the area where the analysis will be conducted.
              <br />
              {isHasAdminArea ? (
                <>
                  Input location using Administrative Area:{" "}
                  {getAdminAreaHierarchyLabel()}
                </>
              ) : (
                <>
                  Input location using a{" "}
                  {templateDetail.configuration.area_input.catchment?.metric ===
                  "time"
                    ? formatTimeCatchment()
                    : formatRadiusCatchment()}{" "}
                  around <b>{formatLocationNames()}</b>
                </>
              )}
            </p>
          </div>
        </div>

        <div>
          <p className="text-txtHeading text-sm font-semibold">Output</p>
          <div className="*:leading-5 *:text-dim-gray *:text-xs">
            {templateDetail.configuration.analysis_output.type ===
            EOutputAnalysisType.PROFILING ? (
              <p>
                Select the output type that fits the analysis objective. Users
                selected profiling to divide the map into uniform spatial
                sections, allowing for clearer area comparisons and more
                reliable location-based insights.
              </p>
            ) : (
              <p data-testid="output-text">
                Divides the{" "}
                {!isHasAdminArea
                  ? formatLocationNames()
                  : adminArea?.data[0]?.name}{" "}
                area into equal-sized hexagonal grids using{" "}
                {
                  LABEL_DATASET_TYPE[
                    templateDetail.configuration.analysis_output
                      .type as SpatialAggregationType
                  ]
                }{" "}
                indexing at level{" "}
                {templateDetail.configuration.analysis_output.grid_level},
                providing a standardized and granular spatial breakdown.
              </p>
            )}
          </div>
        </div>

        <div>
          <p className="text-txtHeading text-sm font-semibold">Weight Output</p>
          <div className="*:leading-5 *:text-dim-gray *:text-xs">
            <p>
              Assign different weight values to reflect each factor's strategic
              importance in the analysis.
            </p>
            <ul className="list-disc list-outside pl-4">
              {Object.entries(templateAttributes).map(([name, weight]) => (
                <li key={name}>
                  {name} - {weight}%
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      <div className="h-5 shrink-0 w-full" />
    </div>
  );
}
